import React, { useState } from 'react';
import { CheckCircle2, Circle, ChevronDown, ChevronUp, Info } from 'lucide-react';
import { ScheduledMeal } from '../types';
import MealDetailsModal from './MealDetailsModal';

interface MealScheduleListProps {
  meals: ScheduledMeal[];
  selectedDate: Date;
}

const MealScheduleList: React.FC<MealScheduleListProps> = ({ meals, selectedDate }) => {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    'morning': true,
    'afternoon': true
  });
  const [selectedMeal, setSelectedMeal] = useState<ScheduledMeal | null>(null);
  const [showModal, setShowModal] = useState(false);

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleMealPrepared = (mealId: number) => {
    // In a real app, this would update the database
    console.log(`Meal ${mealId} marked as prepared`);
  };

  const openMealDetails = (meal: ScheduledMeal) => {
    setSelectedMeal(meal);
    setShowModal(true);
  };

  const morningMeals = meals.filter(meal => meal.shift === 'morning');
  const afternoonMeals = meals.filter(meal => meal.shift === 'afternoon');

  const renderMealCards = (mealsList: ScheduledMeal[]) => {
    if (mealsList.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500">
          No meals scheduled for this period
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-4">
        {mealsList.map(meal => (
          <div 
            key={meal.id}
            className="bg-white rounded-lg shadow p-4 border-l-4 border-primary-500 hover:shadow-md transition-shadow"
          >
            <div className="flex justify-between">
              <h3 className="font-medium text-lg">{meal.mealTitle}</h3>
              <button
                onClick={() => handleMealPrepared(meal.id)}
                className="text-gray-500 hover:text-primary-600"
              >
                {meal.prepared ? (
                  <CheckCircle2 className="h-6 w-6 text-primary-500" />
                ) : (
                  <Circle className="h-6 w-6" />
                )}
              </button>
            </div>
            
            <div className="flex items-center mt-2 text-sm text-gray-600">
              <span className="bg-gray-100 px-2 py-1 rounded mr-2">
                {meal.type}
              </span>
              <span>Quantity: {meal.qty}</span>
            </div>
            
            <div className="mt-3 flex justify-between items-center">
              <div className="text-sm">
                <span className="font-medium">For: </span>
                <span>{meal.kidName}</span>
              </div>
              
              <div className="flex space-x-2">
                <button
                  onClick={() => openMealDetails(meal)}
                  className="p-1 rounded-full hover:bg-gray-100"
                >
                  <Info className="h-5 w-5 text-primary-500" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (meals.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <h2 className="text-2xl font-medium text-gray-800 mb-2">No Meals Scheduled</h2>
        <p className="text-gray-600">
          There are no meals scheduled for {selectedDate.toLocaleDateString()}.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div 
            className="flex justify-between items-center p-4 bg-primary-50 cursor-pointer"
            onClick={() => toggleSection('morning')}
          >
            <h2 className="text-lg font-medium text-primary-800">Morning Shift</h2>
            <button className="text-primary-800">
              {expandedSections.morning ? <ChevronUp /> : <ChevronDown />}
            </button>
          </div>
          
          {expandedSections.morning && (
            <div className="p-4">{renderMealCards(morningMeals)}</div>
          )}
        </div>
        
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div 
            className="flex justify-between items-center p-4 bg-primary-50 cursor-pointer"
            onClick={() => toggleSection('afternoon')}
          >
            <h2 className="text-lg font-medium text-primary-800">Afternoon Shift</h2>
            <button className="text-primary-800">
              {expandedSections.afternoon ? <ChevronUp /> : <ChevronDown />}
            </button>
          </div>
          
          {expandedSections.afternoon && (
            <div className="p-4">{renderMealCards(afternoonMeals)}</div>
          )}
        </div>
      </div>
      
      {showModal && selectedMeal && (
        <MealDetailsModal
          meal={selectedMeal}
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  );
};

export default MealScheduleList;